import React, { useState, useEffect, useRef } from 'react';
import { wooStoresAPI } from '../services/api';
import { formatDateTimeMY } from '../utils/dateUtils';
import PhoneCollisionsPanel from './PhoneCollisionsPanel';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  ShoppingCart,
  Plus,
  ExternalLink,
  Play,
  RefreshCw,
  Settings,
  Trash2,
  Users,
  FileText,
  Loader2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Phone,
  User,
  Filter
} from 'lucide-react';

const WooCommercePage = () => {
  const [activeTab, setActiveTab] = useState('configurations');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [stores, setStores] = useState([]);
  const [syncHistory, setSyncHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [notification, setNotification] = useState(null);

  // Sync issues state
  const [syncIssues, setSyncIssues] = useState([]);
  const [issuesTotal, setIssuesTotal] = useState(0);
  const [issuesPage, setIssuesPage] = useState(1);
  const [issuesTotalPages, setIssuesTotalPages] = useState(0);
  const [issuesTypeCounts, setIssuesTypeCounts] = useState({});
  const [issuesFilter, setIssuesFilter] = useState('');
  const [issuesLoading, setIssuesLoading] = useState(false);

  // Form state for Add
  const [storeName, setStoreName] = useState('');
  const [storeUrl, setStoreUrl] = useState('');
  const [consumerKey, setConsumerKey] = useState('');
  const [consumerSecret, setConsumerSecret] = useState('');
  const [syncInterval, setSyncInterval] = useState('15');

  // Form state for Edit
  const [editStoreName, setEditStoreName] = useState('');
  const [editStoreUrl, setEditStoreUrl] = useState('');
  const [editConsumerKey, setEditConsumerKey] = useState('');
  const [editConsumerSecret, setEditConsumerSecret] = useState('');
  const [editSyncInterval, setEditSyncInterval] = useState('15');

  // Sync progress tracking
  const [syncProgressData, setSyncProgressData] = useState(null);
  const progressIntervalRef = useRef(null);

  const startProgressPolling = (storeId) => {
    if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    progressIntervalRef.current = setInterval(async () => {
      try {
        const res = await wooStoresAPI.getSyncProgress(storeId);
        const data = res.data;
        setSyncProgressData(data);
        if (data.status === 'done' || data.status === 'error' || data.status === 'idle') {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
          fetchStores();
          fetchSyncHistory();
          if (data.status === 'done') {
            showNotification(data.message, 'success');
            setTimeout(() => setSyncProgressData(null), 5000);
          } else if (data.status === 'error') {
            showNotification(data.message, 'error');
            setTimeout(() => setSyncProgressData(null), 8000);
          }
        }
      } catch (e) {
        // ignore polling errors
      }
    }, 2000);
  };

  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    };
  }, []);

  useEffect(() => {
    fetchStores();
    fetchSyncHistory();
    // Fetch issue count for badge
    fetchSyncIssues(1, '');
  }, []);

  useEffect(() => {
    if (activeTab === 'issues') {
      fetchSyncIssues(issuesPage, issuesFilter);
    }
  }, [activeTab, issuesPage, issuesFilter]);

  const fetchStores = async () => {
    try {
      const response = await wooStoresAPI.getAll();
      setStores(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchSyncHistory = async () => {
    try {
      const response = await wooStoresAPI.getSyncHistory();
      setSyncHistory(response.data);
    } catch (error) {
    }
  };

  const fetchSyncIssues = async (page = 1, issueType = '') => {
    setIssuesLoading(true);
    try {
      const response = await wooStoresAPI.getSyncIssues(page, issueType);
      setSyncIssues(response.data.issues || []);
      setIssuesTotal(response.data.total || 0);
      setIssuesTotalPages(response.data.total_pages || 0);
      setIssuesTypeCounts(response.data.type_counts || {});
    } catch (error) {
    } finally {
      setIssuesLoading(false);
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleAddStore = async () => {
    try {
      const newStore = {
        name: storeName,
        url: storeUrl.replace(/\/$/, ''), // Remove trailing slash
        consumer_key: consumerKey,
        consumer_secret: consumerSecret,
        sync_interval: parseInt(syncInterval),
        is_active: false
      };
      const response = await wooStoresAPI.create(newStore);
      setStores([...stores, response.data]);
      setIsAddModalOpen(false);
      resetAddForm();
      showNotification('Store added successfully!');
    } catch (error) {
      showNotification('Failed to add store', 'error');
    }
  };

  const handleEditStore = (store) => {
    setEditingStore(store);
    setEditStoreName(store.name);
    setEditStoreUrl(store.url);
    setEditConsumerKey(store.consumer_key);
    setEditConsumerSecret(store.consumer_secret);
    setEditSyncInterval(String(store.sync_interval));
    setIsEditModalOpen(true);
  };

  const handleUpdateStore = async () => {
    try {
      const updatedData = {
        name: editStoreName,
        url: editStoreUrl.replace(/\/$/, ''),
        consumer_key: editConsumerKey,
        consumer_secret: editConsumerSecret,
        sync_interval: parseInt(editSyncInterval)
      };
      await wooStoresAPI.update(editingStore.id, updatedData);
      setStores(stores.map(s => 
        s.id === editingStore.id ? { ...s, ...updatedData } : s
      ));
      setIsEditModalOpen(false);
      setEditingStore(null);
      showNotification('Store updated successfully!');
    } catch (error) {
      showNotification('Failed to update store', 'error');
    }
  };

  const resetAddForm = () => {
    setStoreName('');
    setStoreUrl('');
    setConsumerKey('');
    setConsumerSecret('');
    setSyncInterval('5');
  };

  const handleToggleActive = async (storeId) => {
    const store = stores.find(s => s.id === storeId);
    try {
      await wooStoresAPI.update(storeId, { is_active: !store.is_active });
      setStores(stores.map(s => 
        s.id === storeId ? { ...s, is_active: !s.is_active } : s
      ));
    } catch (error) {
    }
  };

  const handleRemoveStore = async (storeId) => {
    if (!window.confirm('Are you sure you want to remove this store?')) return;
    
    try {
      await wooStoresAPI.delete(storeId);
      setStores(stores.filter(s => s.id !== storeId));
      showNotification('Store removed successfully!');
    } catch (error) {
      showNotification('Failed to remove store', 'error');
    }
  };

  const handleTestConnection = async (storeId) => {
    setActionLoading({ ...actionLoading, [storeId + '_test']: true });
    try {
      const response = await wooStoresAPI.testConnection(storeId);
      if (response.data.success) {
        showNotification(response.data.message, 'success');
      } else {
        showNotification(response.data.message, 'error');
      }
    } catch (error) {
      showNotification('Failed to test connection', 'error');
    } finally {
      setActionLoading({ ...actionLoading, [storeId + '_test']: false });
    }
  };

  const handleSyncClients = async (storeId) => {
    setActionLoading({ ...actionLoading, [storeId + '_sync']: true });
    showNotification('Sync started — syncing clients and orders in background...', 'success');
    try {
      const response = await wooStoresAPI.sync(storeId);
      if (response.data.success) {
        startProgressPolling(storeId);
      } else {
        showNotification(response.data.message || 'Sync failed', 'error');
      }
      fetchStores();
      fetchSyncHistory();
    } catch (error) {
      showNotification('Sync request failed', 'error');
      fetchSyncHistory();
    } finally {
      setActionLoading({ ...actionLoading, [storeId + '_sync']: false });
    }
  };

  const handleSyncOrders = async (storeId) => {
    setActionLoading({ ...actionLoading, [storeId + '_orders']: true });
    try {
      const response = await wooStoresAPI.syncOrders(storeId);
      if (response.data.success) {
        showNotification(response.data.message, 'success');
      } else {
        showNotification(response.data.message, 'error');
      }
      fetchSyncHistory();  // Refresh sync history
    } catch (error) {
      showNotification('Failed to sync orders', 'error');
      fetchSyncHistory();  // Refresh sync history even on error
    } finally {
      setActionLoading({ ...actionLoading, [storeId + '_orders']: false });
    }
  };

  const handleRecalculateStats = async (storeId) => {
    if (!window.confirm('This will recalculate all client order stats using strict matching (phone AND name/email). Continue?')) return;
    
    setActionLoading({ ...actionLoading, [storeId + '_recalc']: true });
    try {
      const response = await wooStoresAPI.recalculateStats(storeId);
      if (response.data.success) {
        showNotification(response.data.message, 'success');
        startProgressPolling(storeId);
        fetchStores();
      } else {
        showNotification(response.data.message, 'error');
      }
      fetchSyncHistory();  // Refresh sync history
    } catch (error) {
      showNotification('Failed to recalculate stats', 'error');
      fetchSyncHistory();  // Refresh sync history even on error
    } finally {
      setActionLoading({ ...actionLoading, [storeId + '_recalc']: false });
    }
  };

  const handleFullSyncOrders = async (storeId) => {
    if (!window.confirm('This will perform a FULL sync of ALL orders from WooCommerce. This may take several minutes. Continue?')) return;
    
    setActionLoading({ ...actionLoading, [storeId + '_fullsync']: true });
    showNotification('Full sync started. This may take several minutes...', 'success');
    
    try {
      const response = await wooStoresAPI.fullSyncOrders(storeId);
      if (response.data.success) {
        showNotification(response.data.message, 'success');
        fetchStores();
      } else {
        showNotification(response.data.message || 'Full sync failed', 'error');
      }
      fetchSyncHistory();
    } catch (error) {
      showNotification('Full sync failed - check sync history for details', 'error');
      fetchSyncHistory();
    } finally {
      setActionLoading({ ...actionLoading, [storeId + '_fullsync']: false });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-[#4F46E5]" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 ${
          notification.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
        }`}>
          {notification.type === 'success' ? (
            <CheckCircle className="text-green-500" size={20} />
          ) : (
            <XCircle className="text-red-500" size={20} />
          )}
          <span className={notification.type === 'success' ? 'text-green-700' : 'text-red-700'}>
            {notification.message}
          </span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">WooCommerce Integration</h1>
          <p className="text-gray-500">Manage WooCommerce store integrations, sync orders, and monitor inventory</p>
        </div>
        <Button 
          onClick={() => setIsAddModalOpen(true)}
          className="bg-[#4F46E5] hover:bg-[#4338CA] text-white h-10"
        >
          <Plus size={16} className="mr-2" />
          Add Store
        </Button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex gap-6">
          <button
            onClick={() => setActiveTab('configurations')}
            data-testid="tab-configurations"
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'configurations'
                ? 'border-[#4F46E5] text-[#4F46E5]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Store Configurations
          </button>
          <button
            onClick={() => setActiveTab('overview')}
            data-testid="tab-overview"
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'overview'
                ? 'border-[#4F46E5] text-[#4F46E5]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Sync Overview
          </button>
          <button
            onClick={() => setActiveTab('issues')}
            data-testid="tab-sync-issues"
            className={`pb-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === 'issues'
                ? 'border-[#4F46E5] text-[#4F46E5]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <AlertTriangle size={14} />
            Sync Issues
            {issuesTotal > 0 && (
              <span className="bg-amber-100 text-amber-700 text-xs font-medium px-1.5 py-0.5 rounded-full">{issuesTotal}</span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('collisions')}
            data-testid="tab-phone-collisions"
            className={`pb-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === 'collisions'
                ? 'border-[#4F46E5] text-[#4F46E5]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Phone size={14} />
            Phone Collisions
          </button>
        </div>
      </div>

      {/* Store Cards */}
      {activeTab === 'configurations' && (
        <div className="space-y-4">
          {stores.map((store) => (
            <Card key={store.id} className="p-6 bg-white border border-gray-100">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{store.name}</h3>
                  <a 
                    href={store.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-500 text-sm flex items-center gap-1 hover:text-[#4F46E5]"
                  >
                    <ExternalLink size={12} />
                    {store.url}
                  </a>
                </div>
                <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                  store.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {store.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm text-gray-500">Auto-Sync Interval</p>
                  <p className="font-medium text-gray-900">
                    {store.sync_interval >= 60 
                      ? `${store.sync_interval / 60} hour${store.sync_interval > 60 ? 's' : ''}` 
                      : `${store.sync_interval} minute${store.sync_interval > 1 ? 's' : ''}`}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Last Sync</p>
                  <p className="font-medium text-gray-900">{store.last_sync || 'Never'}</p>
                </div>
              </div>

              <div className="flex items-center gap-2 mb-4">
                <Switch
                  checked={store.is_active}
                  onCheckedChange={() => handleToggleActive(store.id)}
                />
                <span className="text-sm text-gray-600">
                  {store.is_active ? 'Auto-Sync Active' : 'Auto-Sync Disabled'}
                </span>
              </div>

              <div className="flex items-center gap-2 mb-4">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="h-9"
                  onClick={() => handleTestConnection(store.id)}
                  disabled={actionLoading[store.id + '_test']}
                >
                  {actionLoading[store.id + '_test'] ? (
                    <Loader2 size={14} className="mr-2 animate-spin" />
                  ) : (
                    <FileText size={14} className="mr-2" />
                  )}
                  Test
                </Button>
                <Button 
                  size="sm" 
                  className="h-9 bg-[#4F46E5] hover:bg-[#4338CA] text-white"
                  onClick={() => handleSyncClients(store.id)}
                  disabled={actionLoading[store.id + '_sync']}
                >
                  {actionLoading[store.id + '_sync'] ? (
                    <Loader2 size={14} className="mr-2 animate-spin" />
                  ) : (
                    <Play size={14} className="mr-2" />
                  )}
                  Sync
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="h-9 border-orange-300 text-orange-600 hover:bg-orange-50"
                  onClick={() => handleRecalculateStats(store.id)}
                  disabled={actionLoading[store.id + '_recalc']}
                  title="Recalculate stats with strict matching (phone AND name/email)"
                >
                  {actionLoading[store.id + '_recalc'] ? (
                    <Loader2 size={14} className="mr-2 animate-spin" />
                  ) : (
                    <RefreshCw size={14} className="mr-2" />
                  )}
                  Fix Stats
                </Button>
              </div>

              {/* Sync Progress Bar */}
              {syncProgressData && syncProgressData.status === 'running' && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100" data-testid="sync-progress">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Loader2 size={14} className="animate-spin text-blue-600" />
                      <span className="text-sm font-medium text-blue-800">{syncProgressData.phase}</span>
                    </div>
                    <span className="text-sm font-bold text-blue-700">{syncProgressData.percent}%</span>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-2.5">
                    <div 
                      className="bg-blue-600 h-2.5 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(syncProgressData.percent, 100)}%` }}
                    />
                  </div>
                  <p className="text-xs text-blue-600 mt-1">{syncProgressData.message}</p>
                </div>
              )}
              {syncProgressData && syncProgressData.status === 'done' && (
                <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-100">
                  <div className="flex items-center gap-2">
                    <CheckCircle size={14} className="text-green-600" />
                    <span className="text-sm font-medium text-green-800">{syncProgressData.message}</span>
                  </div>
                </div>
              )}
              {syncProgressData && syncProgressData.status === 'error' && (
                <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-100">
                  <div className="flex items-center gap-2">
                    <AlertTriangle size={14} className="text-red-600" />
                    <span className="text-sm font-medium text-red-800">{syncProgressData.message}</span>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-4 pt-4 border-t border-gray-100">
                <button 
                  onClick={() => handleEditStore(store)}
                  className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
                >
                  <Settings size={14} />
                  Configure
                </button>
                <button 
                  onClick={() => handleRemoveStore(store.id)}
                  className="flex items-center gap-2 text-sm text-red-500 hover:text-red-600"
                >
                  <Trash2 size={14} />
                  Remove
                </button>
              </div>

              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100 text-sm text-gray-500">
                <Users size={14} />
                {store.clients_count || 0} clients
              </div>
            </Card>
          ))}

          {stores.length === 0 && (
            <Card className="p-8 bg-white border border-gray-100 text-center">
              <p className="text-gray-500">No stores configured. Click "Add Store" to connect your WooCommerce store.</p>
            </Card>
          )}
        </div>
      )}

      {/* Sync Overview Tab */}
      {activeTab === 'overview' && (
        <Card className="p-6 bg-white border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Sync History</h3>
          {syncHistory.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No sync history available. Start syncing your stores to see activity here.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date/Time</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Store</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Records</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Message</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {syncHistory.map((entry) => (
                    <tr key={entry.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {formatDateTimeMY(entry.created_at)}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {entry.store_name}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          entry.sync_type === 'clients' ? 'bg-blue-100 text-blue-700' :
                          entry.sync_type === 'orders' ? 'bg-purple-100 text-purple-700' :
                          'bg-orange-100 text-orange-700'
                        }`}>
                          {entry.sync_type === 'fix_stats' ? 'Fix Stats' : entry.sync_type}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {entry.success ? (
                          <span className="flex items-center gap-1 text-green-600">
                            <CheckCircle size={14} />
                            Success
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-red-600">
                            <XCircle size={14} />
                            Failed
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {entry.records_synced || 0}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate" title={entry.message}>
                        {entry.message}
                        {entry.error_details && (
                          <details className="mt-1">
                            <summary className="text-xs text-red-500 cursor-pointer">View error details</summary>
                            <pre className="mt-1 text-xs text-red-600 bg-red-50 p-2 rounded overflow-auto max-h-32">
                              {entry.error_details}
                            </pre>
                          </details>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Sync Issues Tab */}
      {activeTab === 'issues' && (
        <Card className="bg-white border border-gray-100" data-testid="sync-issues-card">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Sync Issues</h3>
                <p className="text-gray-500 text-sm">Orders with missing or problematic data from WooCommerce ({issuesTotal} total)</p>
              </div>
              <Button variant="outline" onClick={() => fetchSyncIssues(issuesPage, issuesFilter)} disabled={issuesLoading}>
                <RefreshCw size={16} className={`mr-2 ${issuesLoading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            </div>

            {/* Issue type summary pills */}
            {Object.keys(issuesTypeCounts).length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4">
                <button
                  onClick={() => { setIssuesFilter(''); setIssuesPage(1); }}
                  data-testid="filter-all"
                  className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                    !issuesFilter ? 'bg-[#4F46E5] text-white border-[#4F46E5]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                  }`}
                >
                  All ({issuesTotal})
                </button>
                {issuesTypeCounts.masked_phone > 0 && (
                  <button
                    onClick={() => { setIssuesFilter('masked_phone'); setIssuesPage(1); }}
                    data-testid="filter-masked-phone"
                    className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors flex items-center gap-1 ${
                      issuesFilter === 'masked_phone' ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-amber-700 border-amber-200 hover:border-amber-400'
                    }`}
                  >
                    <Phone size={12} /> Masked Phone ({issuesTypeCounts.masked_phone})
                  </button>
                )}
                {issuesTypeCounts.missing_phone > 0 && (
                  <button
                    onClick={() => { setIssuesFilter('missing_phone'); setIssuesPage(1); }}
                    data-testid="filter-missing-phone"
                    className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors flex items-center gap-1 ${
                      issuesFilter === 'missing_phone' ? 'bg-red-500 text-white border-red-500' : 'bg-white text-red-700 border-red-200 hover:border-red-400'
                    }`}
                  >
                    <Phone size={12} /> Missing Phone ({issuesTypeCounts.missing_phone})
                  </button>
                )}
                {issuesTypeCounts.missing_name > 0 && (
                  <button
                    onClick={() => { setIssuesFilter('missing_name'); setIssuesPage(1); }}
                    data-testid="filter-missing-name"
                    className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors flex items-center gap-1 ${
                      issuesFilter === 'missing_name' ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-orange-700 border-orange-200 hover:border-orange-400'
                    }`}
                  >
                    <User size={12} /> Missing Name ({issuesTypeCounts.missing_name})
                  </button>
                )}
              </div>
            )}
          </div>

          {issuesLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-[#4F46E5]" size={32} />
            </div>
          ) : syncIssues.length === 0 ? (
            <div className="p-12 text-center">
              <CheckCircle className="mx-auto text-green-300 mb-4" size={48} />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Sync Issues</h3>
              <p className="text-gray-500">All orders synced cleanly. Run a "Sync" to check for data quality issues.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Order</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Customer</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Phone</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Issue</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Source</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {syncIssues.map((issue) => (
                    <tr key={issue.id} className="hover:bg-gray-50" data-testid={`issue-row-${issue.id}`}>
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900 text-sm">#{issue.order_number}</p>
                        <p className="text-xs text-gray-400">{issue.order_date ? formatDateTimeMY(issue.order_date) : '-'}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-900">{issue.customer_name}</p>
                        {issue.email && <p className="text-xs text-gray-400">{issue.email}</p>}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono">
                        {issue.phone || '-'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${
                          issue.issue_type === 'masked_phone' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                          issue.issue_type === 'missing_phone' ? 'bg-red-50 text-red-700 border border-red-200' :
                          'bg-orange-50 text-orange-700 border border-orange-200'
                        }`}>
                          {issue.issue_type === 'masked_phone' && <><Phone size={10} /> Masked Phone</>}
                          {issue.issue_type === 'missing_phone' && <><Phone size={10} /> No Phone</>}
                          {issue.issue_type === 'missing_name' && <><User size={10} /> No Name</>}
                        </span>
                        <p className="text-xs text-gray-400 mt-1">{issue.issue_detail}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          issue.order_source === 'Shopee' ? 'bg-orange-100 text-orange-700' :
                          issue.order_source === 'Lazada' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {issue.order_source || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                        RM {(issue.order_total || 0).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {issuesTotalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100" data-testid="pagination-sync-issues">
              <p className="text-sm text-gray-500">
                Page {issuesPage} of {issuesTotalPages} ({issuesTotal} total)
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIssuesPage(p => Math.max(1, p - 1))}
                  disabled={issuesPage <= 1}
                >
                  <ChevronLeft size={14} className="mr-1" /> Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIssuesPage(p => Math.min(issuesTotalPages, p + 1))}
                  disabled={issuesPage >= issuesTotalPages}
                >
                  Next <ChevronRight size={14} className="ml-1" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Phone Collisions Tab */}
      {activeTab === 'collisions' && (
        <PhoneCollisionsPanel />
      )}


      {/* Add Store Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Add WooCommerce Store</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Store Name *</Label>
              <Input
                placeholder="My WooCommerce Store"
                value={storeName}
                onChange={(e) => setStoreName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Store URL *</Label>
              <Input
                placeholder="https://yourstore.com"
                value={storeUrl}
                onChange={(e) => setStoreUrl(e.target.value)}
              />
              <p className="text-xs text-red-500">Without trailing slash</p>
            </div>

            <div className="space-y-2">
              <Label>Consumer Key *</Label>
              <Input
                placeholder="ck_..."
                value={consumerKey}
                onChange={(e) => setConsumerKey(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Consumer Secret *</Label>
              <Input
                placeholder="cs_..."
                type="password"
                value={consumerSecret}
                onChange={(e) => setConsumerSecret(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Auto-Sync Interval</Label>
              <select
                value={syncInterval}
                onChange={(e) => setSyncInterval(e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5]"
              >
                <option value="1">Every 1 minute</option>
                <option value="5">Every 5 minutes</option>
                <option value="15">Every 15 minutes</option>
                <option value="30">Every 30 minutes</option>
                <option value="60">Every 1 hour</option>
              </select>
              <p className="text-xs text-gray-500">Automatic sync runs when "Active" is enabled. Uses incremental sync (only new/modified data).</p>
            </div>

            <Button
              onClick={handleAddStore}
              className="w-full bg-[#4F46E5] hover:bg-[#4338CA] text-white"
              disabled={!storeName || !storeUrl || !consumerKey || !consumerSecret}
            >
              Add Store
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Store Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Configure Store</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Store Name *</Label>
              <Input
                placeholder="My WooCommerce Store"
                value={editStoreName}
                onChange={(e) => setEditStoreName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Store URL *</Label>
              <Input
                placeholder="https://yourstore.com"
                value={editStoreUrl}
                onChange={(e) => setEditStoreUrl(e.target.value)}
              />
              <p className="text-xs text-red-500">Without trailing slash</p>
            </div>

            <div className="space-y-2">
              <Label>Consumer Key *</Label>
              <Input
                placeholder="ck_..."
                value={editConsumerKey}
                onChange={(e) => setEditConsumerKey(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Consumer Secret *</Label>
              <Input
                placeholder="cs_..."
                type="password"
                value={editConsumerSecret}
                onChange={(e) => setEditConsumerSecret(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Auto-Sync Interval</Label>
              <select
                value={editSyncInterval}
                onChange={(e) => setEditSyncInterval(e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5]"
              >
                <option value="1">Every 1 minute</option>
                <option value="5">Every 5 minutes</option>
                <option value="15">Every 15 minutes</option>
                <option value="30">Every 30 minutes</option>
                <option value="60">Every 1 hour</option>
              </select>
              <p className="text-xs text-gray-500">Automatic sync runs when "Active" is enabled. Uses incremental sync (only new/modified data).</p>
            </div>

            <Button
              onClick={handleUpdateStore}
              className="w-full bg-[#4F46E5] hover:bg-[#4338CA] text-white"
              disabled={!editStoreName || !editStoreUrl || !editConsumerKey || !editConsumerSecret}
            >
              Update Store
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WooCommercePage;
